/**
 * Coordonnées d'un formulaire de contact réglables dans le Studio : pour le
 * nom, l'e-mail et le téléphone, « required » (affiché et obligatoire),
 * « optional » (affiché et facultatif) ou « hidden » (masqué).
 *
 * Mêmes réglages (`field_name`, `field_email`, `field_phone`) sur tous les
 * moteurs. Utilisé par le formulaire « demande de devis »
 * (themes/default/sections/ContactQuoteStepper.jsx).
 */

const MODES = new Set(["required", "optional", "hidden"]);
const DEFAULTS = { name: "required", email: "required", phone: "optional" };

function mode(value, fallback) {
  return MODES.has(value) ? value : fallback;
}

/**
 * Modes effectifs. Le serveur refuse une demande sans e-mail ni téléphone :
 * si les deux sont masqués, l'e-mail est réaffiché (obligatoire) plutôt que
 * de laisser un formulaire impossible à envoyer.
 */
export function contactFieldModes(settings = {}) {
  const modes = {
    name: mode(settings.field_name, DEFAULTS.name),
    email: mode(settings.field_email, DEFAULTS.email),
    phone: mode(settings.field_phone, DEFAULTS.phone),
  };
  if (modes.email === "hidden" && modes.phone === "hidden") modes.email = "required";
  return modes;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const digits = (v) => String(v || "").replace(/\D/g, "");

/**
 * Problème bloquant des coordonnées saisies, ou "" si elles sont valides.
 * Règles : champs obligatoires remplis, e-mail bien formé s'il est saisi,
 * téléphone d'au moins 6 chiffres s'il est saisi, et au moins un moyen de
 * recontacter le visiteur (e-mail ou téléphone).
 */
export function contactFieldsError(form, modes) {
  const name = String(form.name || "").trim();
  const email = modes.email === "hidden" ? "" : String(form.email || "").trim();
  const phone = modes.phone === "hidden" ? "" : String(form.phone || "").trim();
  if (modes.name === "required" && !name) return "Indiquez votre nom.";
  if (modes.email === "required" && !email) return "Indiquez votre adresse e-mail.";
  if (modes.phone === "required" && !phone) return "Indiquez votre numéro de téléphone.";
  if (email && !EMAIL_RE.test(email)) return "Adresse e-mail invalide.";
  if (phone && digits(phone).length < 6) return "Numéro de téléphone invalide.";
  if (!email && !phone) return "Indiquez au moins un e-mail ou un numéro de téléphone.";
  return "";
}

/** Texte indicatif d'un champ : « (optionnel) » quand il est facultatif. */
export function contactPlaceholder(label, fieldMode) {
  return fieldMode === "optional" ? `${label} (optionnel)` : label;
}

/** Valeurs à envoyer : un champ masqué n'envoie rien, même s'il reste en mémoire. */
export function contactPayload(form, modes) {
  return {
    name: modes.name === "hidden" ? "" : String(form.name || "").trim(),
    email: modes.email === "hidden" ? "" : String(form.email || "").trim(),
    phone: modes.phone === "hidden" ? "" : String(form.phone || "").trim(),
  };
}
